import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import './BlindClosingModal.css';

/**
 * BlindClosingModal - 3-Step Closing Wizard
 * Step 1: Bill Breakdown (Blind Cash Count) + Base for Tomorrow (denomination)
 * Step 2: Electronic Payments Review (Datáfono, Nequi, Transferencias)
 * Step 3: PIN + Confirmation
 * 
 * [Refactored April 8, 2026] Added Step 2 to reconcile electronic payments
 * [Refactored April 21, 2026] Added HelaUp-style denomination base selection
 *   with auto-fill and explicit "deliver vs keep" bill breakdown
 */
const BlindClosingModal = ({ session, onClose, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [breakdown, setBreakdown] = useState({
        '100k': '',
        '50k': '',
        '20k': '',
        '10k': '',
        '5k': '',
        '2k': '',
        '1k': '',
        'coins': ''
    });
    // Base denomination breakdown (kept for next day)
    const [keptBreakdown, setKeptBreakdown] = useState({
        '100k': '',
        '50k': '',
        '20k': '',
        '10k': '',
        '5k': '',
        '2k': '',
        '1k': '',
        'coins': ''
    });
    const [targetKept, setTargetKept] = useState('100000');
    const [pin, setPin] = useState('');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // Electronic payments state
    const [electronicPayments, setElectronicPayments] = useState(null);
    const [loadingElectronic, setLoadingElectronic] = useState(false);

    const denominations = [
        { key: '100k', label: '$100.000', value: 100000 },
        { key: '50k', label: '$50.000', value: 50000 },
        { key: '20k', label: '$20.000', value: 20000 },
        { key: '10k', label: '$10.000', value: 10000 },
        { key: '5k', label: '$5.000', value: 5000 },
        { key: '2k', label: '$2.000', value: 2000 },
        { key: '1k', label: '$1.000', value: 1000 },
        { key: 'coins', label: 'Monedas', value: 1 }
    ];

    // Fetch electronic payments when entering Step 2
    useEffect(() => {
        if (step === 2 && !electronicPayments) {
            fetchElectronicPayments();
        }
    }, [step]);

    const fetchElectronicPayments = async () => {
        setLoadingElectronic(true);
        try {
            const response = await api.get('/cash-box/electronic-payments');
            setElectronicPayments(response.data);
        } catch (err) {
            console.error('Error fetching electronic payments:', err);
            setElectronicPayments({ payments: [], summary: { total: 0, count: 0, by_method: [] } });
        } finally {
            setLoadingElectronic(false);
        }
    };

    const handleBreakdownChange = (denomination, value) => {
        setBreakdown(prev => ({
            ...prev,
            [denomination]: value
        }));
    };

    const handleKeptChange = (denomination, value) => {
        // Don't allow keeping more than counted
        const countedQty = parseInt(breakdown[denomination] || 0);
        const newQty = Math.min(parseInt(value || 0), countedQty);
        setKeptBreakdown(prev => ({
            ...prev,
            [denomination]: denomination === 'coins' ? value : (newQty >= 0 ? String(newQty) : '')
        }));
    };

    const calculateTotal = (bd) => {
        let total = 0;
        for (const [denom, qty] of Object.entries(bd)) {
            if (denom === 'coins') {
                total += parseFloat(qty || 0);
            } else {
                const denomObj = denominations.find(d => d.key === denom);
                total += (denomObj?.value || 0) * parseInt(qty || 0);
            }
        }
        return total;
    };

    const cashTotal = useMemo(() => calculateTotal(breakdown), [breakdown]);
    const keptTotal = useMemo(() => calculateTotal(keptBreakdown), [keptBreakdown]);
    const deliverTotal = useMemo(() => cashTotal - keptTotal, [cashTotal, keptTotal]);

    // Auto-fill: prioritize coins and small bills for the base
    const handleAutoFill = () => {
        const target = parseInt(targetKept || 0);
        if (target <= 0) return;

        // Build available counts from the counted breakdown (ascending by value)
        const sorted = [...denominations].reverse(); // coins, 1k, 2k, ... 100k
        const countedMap = {};
        sorted.forEach(d => {
            countedMap[d.key] = d.key === 'coins' 
                ? parseFloat(breakdown[d.key] || 0) 
                : parseInt(breakdown[d.key] || 0);
        });

        const newKept = {};
        denominations.forEach(d => { newKept[d.key] = ''; });

        let remaining = target;

        // First pass: smallest first
        for (const d of sorted) {
            if (remaining <= 0) break;
            const available = countedMap[d.key] || 0;
            if (available <= 0) continue;

            if (d.key === 'coins') {
                // For coins, use the direct value (up to remaining)
                const useCoins = Math.min(available, remaining);
                if (useCoins > 0) {
                    newKept[d.key] = String(useCoins);
                    remaining -= useCoins;
                }
            } else {
                if (d.value > remaining) continue;
                const needed = Math.floor(remaining / d.value);
                const use = Math.min(needed, available);
                if (use > 0) {
                    newKept[d.key] = String(use);
                    remaining -= use * d.value;
                }
            }
        }

        // Second pass: try larger bills if still remaining
        if (remaining > 0) {
            for (const d of [...denominations]) { // largest first
                if (remaining <= 0) break;
                const available = (countedMap[d.key] || 0) - parseInt(newKept[d.key] || 0);
                if (available <= 0 || d.key === 'coins' || d.value > remaining) continue;
                const needed = Math.floor(remaining / d.value);
                const use = Math.min(needed, available);
                if (use > 0) {
                    newKept[d.key] = String(parseInt(newKept[d.key] || 0) + use);
                    remaining -= use * d.value;
                }
            }
        }

        setKeptBreakdown(newKept);
    };

    const handleClearKept = () => {
        const empty = {};
        denominations.forEach(d => { empty[d.key] = ''; });
        setKeptBreakdown(empty);
    };

    const handleGoToStep2 = () => {
        if (cashTotal === 0) {
            setError('Debe ingresar el conteo físico de billetes');
            return;
        }
        if (keptTotal > cashTotal) {
            setError('La base no puede ser mayor al total contado');
            return;
        }
        setError(null);
        setStep(2);
    };

    const handleGoToStep3 = () => {
        setError(null);
        setStep(3);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!pin || pin.length !== 4) {
                setError('Debe ingresar un PIN de 4 dígitos');
                setLoading(false);
                return;
            }

            const response = await api.post('/cash-box/close', {
                breakdown,
                pin,
                notes,
                base_left: keptTotal
            });

            setResult({ ...response.data.result, keptBreakdown, keptTotal });
        } catch (err) {
            console.error('Error closing cash box:', err);
            setError(err.response?.data?.error || 'Error al cerrar caja');
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return `$${Math.round(parseFloat(amount || 0)).toLocaleString('es-CO')}`;
    };

    const getPaymentMethodLabel = (method) => {
        const labels = {
            'transferencia': '🏦 Transferencia',
            'nequi': '📱 Nequi',
            'datafono': '💳 Datáfono',
            'pago_electronico': '💳 Pago Electrónico',
            'nota_credito': '📝 Nota Crédito',
            'bancolombia': '🏦 Bancolombia',
            'daviplata': '📱 Daviplata',
        };
        return labels[method] || method?.replace(/_/g, ' ') || 'Otro';
    };

    // Calculate deliver breakdown (counted minus kept per denomination)
    const getDeliverBreakdown = () => {
        return denominations.map(d => {
            const counted = d.key === 'coins' 
                ? parseFloat(breakdown[d.key] || 0) 
                : parseInt(breakdown[d.key] || 0);
            const kept = d.key === 'coins' 
                ? parseFloat(keptBreakdown[d.key] || 0) 
                : parseInt(keptBreakdown[d.key] || 0);
            const deliver = counted - kept;
            return { ...d, counted, kept, deliver };
        }).filter(d => d.deliver > 0 || d.kept > 0);
    };

    // ─── RESULT SCREEN ───
    if (result) {
        const absDiff = Math.abs(result.difference);
        const isDifferent = absDiff > 100;
        const deliverBreakdown = getDeliverBreakdown();
        const keptItems = deliverBreakdown.filter(d => d.kept > 0);
        const deliverItems = deliverBreakdown.filter(d => d.deliver > 0);

        return (
            <div className="modal-overlay">
                <div className="modal-container result-container">
                    <div className="modal-header">
                        <h2>📊 Resultado del Cierre</h2>
                    </div>

                    <div className="modal-body">
                        {/* Balance comparison */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="result-card">
                                <div className="text-sm text-gray-600">Efectivo Contado</div>
                                <div className="text-3xl font-bold text-blue-700">
                                    {formatCurrency(result.closing_balance_declared)}
                                </div>
                            </div>
                            <div className="result-card">
                                <div className="text-sm text-gray-600">Saldo Esperado</div>
                                <div className="text-3xl font-bold text-gray-700">
                                    {formatCurrency(result.closing_balance_expected)}
                                </div>
                            </div>
                        </div>

                        {/* Difference alert */}
                        {isDifferent && (
                            <div className={`alert-box ${result.difference > 0 ? 'alert-warning' : 'alert-error'} mb-6`}>
                                <div className="text-lg font-bold">
                                    ⚠️ DESCUADRE DETECTADO
                                </div>
                                <div className="text-2xl font-bold mt-2">
                                    Diferencia: {formatCurrency(absDiff)}
                                    {result.difference > 0 ? ' (Sobrante)' : ' (Faltante)'}
                                </div>
                            </div>
                        )}

                        {!isDifferent && (
                            <div className="alert-box alert-success mb-6">
                                <div className="text-lg font-bold">
                                    ✅ CAJA CUADRADA
                                </div>
                                <div className="text-md mt-1">
                                    El efectivo contado coincide con el saldo esperado
                                </div>
                            </div>
                        )}

                        {/* ═══ BASE PARA MAÑANA (denomination detail) ═══ */}
                        {keptItems.length > 0 && (
                            <div className="p-4 rounded-lg mb-4" style={{ background: '#f0fdf4', border: '2px solid #86efac' }}>
                                <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                                    <span style={{ fontSize: '1.3em' }}>🏦</span> Base para Mañana — {formatCurrency(result.keptTotal)}
                                </h3>
                                <p className="text-xs text-green-600 mb-3">
                                    Deje estos billetes y monedas en la caja para el siguiente turno:
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {keptItems.map(d => (
                                        <div key={d.key} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '6px 12px', background: '#dcfce7', borderRadius: '8px',
                                            fontSize: '14px'
                                        }}>
                                            <span style={{ fontWeight: 600, color: '#166534' }}>
                                                {d.key === 'coins' ? '🪙 Monedas' : `💵 ${d.label}`}
                                            </span>
                                            <span style={{ fontWeight: 700, color: '#15803d', fontSize: '15px' }}>
                                                {d.key === 'coins' 
                                                    ? formatCurrency(d.kept)
                                                    : `${d.kept} × ${d.label} = ${formatCurrency(d.kept * d.value)}`
                                                }
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ═══ A ENTREGAR / GUARDAR ═══ */}
                        {deliverItems.length > 0 && (
                            <div className="p-4 rounded-lg mb-4" style={{ background: '#fef2f2', border: '2px solid #fca5a5' }}>
                                <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                                    <span style={{ fontSize: '1.3em' }}>📦</span> Entregar / Guardar — {formatCurrency(deliverTotal)}
                                </h3>
                                <p className="text-xs text-red-600 mb-3">
                                    Saque estos billetes y monedas de la caja para entregar:
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {deliverItems.map(d => (
                                        <div key={d.key} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '6px 12px', background: '#fee2e2', borderRadius: '8px',
                                            fontSize: '14px'
                                        }}>
                                            <span style={{ fontWeight: 600, color: '#991b1b' }}>
                                                {d.key === 'coins' ? '🪙 Monedas' : `💵 ${d.label}`}
                                            </span>
                                            <span style={{ fontWeight: 700, color: '#b91c1c', fontSize: '15px' }}>
                                                {d.key === 'coins'
                                                    ? formatCurrency(d.deliver)
                                                    : `${d.deliver} × ${d.label} = ${formatCurrency(d.deliver * d.value)}`
                                                }
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Electronic payments in closing result */}
                        {result.electronic_total > 0 && (
                            <div className="p-4 rounded-lg mb-6" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                                <h3 className="font-bold text-teal-800 mb-2">💳 Pagos Electrónicos Registrados</h3>
                                <div className="text-2xl font-bold text-teal-700">{formatCurrency(result.electronic_total)}</div>
                                {result.electronic_by_method && result.electronic_by_method.map((m, i) => (
                                    <div key={i} className="flex justify-between text-sm mt-1">
                                        <span className="text-teal-600">{getPaymentMethodLabel(m.payment_method)}</span>
                                        <span className="font-bold text-teal-700">{formatCurrency(m.total)} ({m.count})</span>
                                    </div>
                                ))}
                                <p className="text-xs text-teal-500 mt-2">
                                    Estos pagos no afectan el conteo de efectivo físico
                                </p>
                            </div>
                        )}

                        {/* Breakdown detail */}
                        <div className="closing-detail-card">
                            <h3 className="font-bold mb-3">Detalle del Día</h3>
                            <table className="w-full text-sm">
                                <tbody>
                                    <tr>
                                        <td className="py-2">Base inicial</td>
                                        <td className="text-right font-semibold">
                                            {formatCurrency(result.opening_balance)}
                                        </td>
                                    </tr>
                                    <tr className="text-green-700">
                                        <td className="py-2">+ Ingresos (efectivo)</td>
                                        <td className="text-right font-semibold">
                                            +{formatCurrency(result.total_income)}
                                        </td>
                                    </tr>
                                    <tr className="text-red-700">
                                        <td className="py-2">- Egresos</td>
                                        <td className="text-right font-semibold">
                                            -{formatCurrency(result.total_expenses)}
                                        </td>
                                    </tr>
                                    <tr className="border-t text-lg font-bold">
                                        <td className="py-2">Saldo esperado</td>
                                        <td className="text-right">
                                            {formatCurrency(result.closing_balance_expected)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button
                            onClick={() => {
                                onSuccess();
                                onClose();
                            }}
                            className="btn-primary w-full"
                        >
                            ✅ Cerrar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── STEP INDICATOR ───
    const StepIndicator = () => (
        <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3].map(s => (
                <React.Fragment key={s}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all ${
                        s === step ? 'bg-blue-600 text-white shadow-lg scale-110' :
                        s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                        {s < step ? '✓' : s}
                    </div>
                    {s < 3 && (
                        <div className={`w-8 h-0.5 ${s < step ? 'bg-green-400' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    // Helper: denomination subtotal display
    const DenomSubtotal = ({ denom, qty }) => {
        const val = denom === 'coins' ? parseFloat(qty || 0) : (denominations.find(d => d.key === denom)?.value || 0) * parseInt(qty || 0);
        return <span style={{ fontWeight: 700, fontSize: '14px', minWidth: '80px', textAlign: 'right', color: val > 0 ? '#d97706' : '#ccc' }}>
            {val > 0 ? formatCurrency(val) : '-'}
        </span>;
    };

    const KeptSubtotal = ({ denom, qty }) => {
        const val = denom === 'coins' ? parseFloat(qty || 0) : (denominations.find(d => d.key === denom)?.value || 0) * parseInt(qty || 0);
        return <span style={{ fontWeight: 700, fontSize: '14px', minWidth: '80px', textAlign: 'right', color: val > 0 ? '#16a34a' : '#ccc' }}>
            {val > 0 ? formatCurrency(val) : '-'}
        </span>;
    };

    // ─── STEP 1: Cash Count + Base for Tomorrow ───
    if (step === 1) {
        return (
            <div className="modal-overlay">
                <div className="modal-container" style={{ maxWidth: '720px' }}>
                    <div className="modal-header">
                        <h2>🔒 Cerrar Caja — Paso 1: Conteo y Fondo</h2>
                        <button onClick={onClose} className="close-button">×</button>
                    </div>

                    <div className="modal-body">
                        <StepIndicator />

                        {/* ═══ TWO COLUMNS: Count + Kept ═══ */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            
                            {/* LEFT: Conteo */}
                            <div style={{ background: '#fffbeb', border: '2px solid #fcd34d', borderRadius: '12px', padding: '12px' }}>
                                <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    💰 Conteo Total
                                </h3>
                                <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#b45309' }}>
                                    Cuente TODO el efectivo en caja
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {denominations.map(d => (
                                        <div key={d.key} style={{
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            padding: '4px 8px', background: '#fefce8', borderRadius: '6px',
                                            border: '1px solid #fef08a'
                                        }}>
                                            <span style={{ fontWeight: 600, fontSize: '13px', minWidth: '70px', color: '#713f12' }}>
                                                {d.key === 'coins' ? 'Monedas' : d.label}
                                            </span>
                                            <span style={{ color: '#999', fontSize: '12px' }}>×</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step={d.key === 'coins' ? '100' : '1'}
                                                value={breakdown[d.key]}
                                                onChange={(e) => handleBreakdownChange(d.key, e.target.value)}
                                                onFocus={(e) => e.target.select()}
                                                placeholder="0"
                                                style={{
                                                    width: '55px', textAlign: 'center', fontWeight: 700, fontSize: '15px',
                                                    padding: '3px 4px', border: '2px solid #e5e7eb', borderRadius: '6px',
                                                    outline: 'none'
                                                }}
                                            />
                                            <span style={{ color: '#999', fontSize: '12px' }}>=</span>
                                            <DenomSubtotal denom={d.key} qty={breakdown[d.key]} />
                                        </div>
                                    ))}
                                </div>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: '#fef3c7', color: '#92400e', padding: '8px 12px', borderRadius: '8px',
                                    marginTop: '8px', fontWeight: 700, fontSize: '16px'
                                }}>
                                    <span>TOTAL:</span>
                                    <span>{formatCurrency(cashTotal)}</span>
                                </div>
                            </div>

                            {/* RIGHT: Base para mañana */}
                            <div style={{ background: '#f0fdf4', border: '2px solid #86efac', borderRadius: '12px', padding: '12px' }}>
                                <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    🏦 Fondo para Mañana
                                </h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#15803d' }}>¿Cuánto dejar?</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ color: '#999', fontSize: '13px' }}>$</span>
                                        <input
                                            type="number"
                                            value={targetKept}
                                            onChange={(e) => setTargetKept(e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                            style={{
                                                width: '90px', textAlign: 'right', fontWeight: 700, fontSize: '14px',
                                                padding: '3px 6px', border: '2px solid #86efac', borderRadius: '6px'
                                            }}
                                        />
                                    </div>
                                    <button type="button" onClick={handleAutoFill} style={{
                                        background: '#22c55e', color: 'white', border: 'none', borderRadius: '6px',
                                        padding: '4px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        ✨ Auto-llenar
                                    </button>
                                    <button type="button" onClick={handleClearKept} style={{
                                        background: '#e5e7eb', color: '#6b7280', border: 'none', borderRadius: '6px',
                                        padding: '4px 8px', fontSize: '11px', cursor: 'pointer'
                                    }}>
                                        🧹
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {denominations.map(d => {
                                        const maxQty = d.key === 'coins' ? parseFloat(breakdown[d.key] || 0) : parseInt(breakdown[d.key] || 0);
                                        return (
                                            <div key={d.key} style={{
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                padding: '4px 8px', background: '#dcfce7', borderRadius: '6px',
                                                border: '1px solid #bbf7d0',
                                                opacity: maxQty > 0 ? 1 : 0.4
                                            }}>
                                                <span style={{ fontWeight: 600, fontSize: '13px', minWidth: '70px', color: '#166534' }}>
                                                    {d.key === 'coins' ? 'Monedas' : d.label}
                                                </span>
                                                <span style={{ color: '#999', fontSize: '12px' }}>×</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={maxQty}
                                                    step={d.key === 'coins' ? '100' : '1'}
                                                    value={keptBreakdown[d.key]}
                                                    onChange={(e) => handleKeptChange(d.key, e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    placeholder="0"
                                                    disabled={maxQty <= 0}
                                                    style={{
                                                        width: '55px', textAlign: 'center', fontWeight: 700, fontSize: '15px',
                                                        padding: '3px 4px', border: '2px solid #86efac', borderRadius: '6px',
                                                        outline: 'none', background: maxQty > 0 ? 'white' : '#f3f4f6'
                                                    }}
                                                />
                                                <span style={{ color: '#999', fontSize: '12px' }}>=</span>
                                                <KeptSubtotal denom={d.key} qty={keptBreakdown[d.key]} />
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: '#bbf7d0', color: '#166534', padding: '8px 12px', borderRadius: '8px',
                                    marginTop: '8px', fontWeight: 700, fontSize: '16px'
                                }}>
                                    <span>BASE:</span>
                                    <span>{formatCurrency(keptTotal)}</span>
                                </div>
                            </div>
                        </div>

                        {/* ═══ SUMMARY: Counted / Kept / Deliver ═══ */}
                        <div style={{
                            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px'
                        }}>
                            <div style={{
                                textAlign: 'center', padding: '10px', borderRadius: '10px',
                                background: '#fffbeb', border: '2px solid #fcd34d'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase' }}>
                                    💰 Total Contado
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: '#b45309', marginTop: '2px' }}>
                                    {formatCurrency(cashTotal)}
                                </div>
                            </div>
                            <div style={{
                                textAlign: 'center', padding: '10px', borderRadius: '10px',
                                background: '#f0fdf4', border: '2px solid #86efac'
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#166534', textTransform: 'uppercase' }}>
                                    🏦 Queda en Caja
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: '#15803d', marginTop: '2px' }}>
                                    {formatCurrency(keptTotal)}
                                </div>
                            </div>
                            <div style={{
                                textAlign: 'center', padding: '10px', borderRadius: '10px',
                                background: deliverTotal < 0 ? '#fef2f2' : '#fff1f2', 
                                border: `2px solid ${deliverTotal < 0 ? '#f87171' : '#fda4af'}`
                            }}>
                                <div style={{ fontSize: '11px', fontWeight: 700, color: '#9f1239', textTransform: 'uppercase' }}>
                                    📦 Entrega
                                </div>
                                <div style={{ 
                                    fontSize: '20px', fontWeight: 800, marginTop: '2px',
                                    color: deliverTotal < 0 ? '#dc2626' : '#be123c'
                                }}>
                                    {formatCurrency(deliverTotal)}
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="alert-box alert-error mb-4">{error}</div>
                        )}

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleGoToStep2}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                            >
                                Siguiente → Pagos Electrónicos
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ─── STEP 2: Electronic Payments ───
    if (step === 2) {
        return (
            <div className="modal-overlay">
                <div className="modal-container">
                    <div className="modal-header">
                        <h2>🔒 Cerrar Caja — Paso 2: Pagos Electrónicos</h2>
                        <button onClick={onClose} className="close-button">×</button>
                    </div>

                    <div className="modal-body">
                        <StepIndicator />

                        <div className="p-4 rounded-lg mb-4" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                            <p className="text-sm text-teal-700">
                                <strong>💳 Pagos Electrónicos (Datáfono, Nequi, Transferencias)</strong><br/>
                                Estos pagos se registraron durante la sesión pero <strong>no afectan el efectivo físico</strong> en caja.
                                Se muestran para su verificación y trazabilidad.
                            </p>
                        </div>

                        {loadingElectronic ? (
                            <div className="text-center py-8 text-gray-500">
                                <div className="text-2xl mb-2">⏳</div>
                                Cargando pagos electrónicos...
                            </div>
                        ) : electronicPayments?.summary?.total > 0 ? (
                            <>
                                {/* Summary cards */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="p-4 rounded-lg text-center" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
                                        <div className="text-xs text-teal-600 font-bold uppercase">Monto Total Electrónico</div>
                                        <div className="text-2xl font-bold text-teal-700 mt-1">
                                            {formatCurrency(electronicPayments.summary.total)}
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-lg text-center" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                        <div className="text-xs text-blue-600 font-bold uppercase">Operaciones</div>
                                        <div className="text-2xl font-bold text-blue-700 mt-1">
                                            {electronicPayments.summary.count}
                                        </div>
                                    </div>
                                </div>

                                {/* Per-method breakdown */}
                                <div className="mb-4">
                                    <h3 className="font-bold text-gray-700 mb-2 text-sm">Desglose por Método</h3>
                                    {electronicPayments.summary.by_method.map((m, i) => (
                                        <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100">
                                            <span className="text-gray-700 font-medium">
                                                {getPaymentMethodLabel(m.payment_method)}
                                            </span>
                                            <div className="text-right">
                                                <span className="text-lg font-bold text-teal-700">{formatCurrency(m.total)}</span>
                                                <span className="text-xs text-gray-500 ml-2">({m.count} ops)</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Payment list (collapsed by default if too many) */}
                                {electronicPayments.payments.length > 0 && (
                                    <details className="mb-4">
                                        <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800">
                                            📋 Ver detalle de {electronicPayments.payments.length} pagos electrónicos
                                        </summary>
                                        <div className="mt-2 max-h-48 overflow-y-auto">
                                            <table className="w-full text-xs">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="py-1 px-2 text-left">Pedido</th>
                                                        <th className="py-1 px-2 text-left">Cliente</th>
                                                        <th className="py-1 px-2 text-left">Método</th>
                                                        <th className="py-1 px-2 text-right">Monto</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {electronicPayments.payments.map((p, i) => (
                                                        <tr key={i} className="border-b border-gray-50">
                                                            <td className="py-1 px-2 font-mono">{p.order_number}</td>
                                                            <td className="py-1 px-2 truncate max-w-[120px]">{p.customer_name}</td>
                                                            <td className="py-1 px-2 capitalize">{p.payment_method?.replace(/_/g, ' ')}</td>
                                                            <td className="py-1 px-2 text-right font-semibold">{formatCurrency(p.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </details>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                <div className="text-3xl mb-2">📭</div>
                                <p className="text-sm">No se encontraron pagos electrónicos durante este turno.</p>
                                <p className="text-xs mt-1">Monto Total Electrónico: $0</p>
                            </div>
                        )}

                        <div className="flex gap-3 mt-4">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                            >
                                ← Volver
                            </button>
                            <button
                                type="button"
                                onClick={handleGoToStep3}
                                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                            >
                                Siguiente → Confirmar Cierre
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ─── STEP 3: PIN & Confirm ───
    return (
        <div className="modal-overlay">
            <div className="modal-container">
                <div className="modal-header">
                    <h2>🔒 Cerrar Caja — Paso 3: Confirmar Cierre</h2>
                    <button onClick={onClose} className="close-button">×</button>
                </div>

                <div className="modal-body">
                    <StepIndicator />

                    {/* Summary before close */}
                    <div className="p-4 rounded-lg mb-4" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <h3 className="font-bold text-blue-800 mb-2">📋 Resumen</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="text-gray-600">💵 Efectivo contado:</div>
                            <div className="text-right font-bold text-blue-700">{formatCurrency(cashTotal)}</div>
                            
                            <div className="text-gray-600">🏦 Base para mañana:</div>
                            <div className="text-right font-bold text-green-700">{formatCurrency(keptTotal)}</div>
                            
                            <div className="text-gray-600">📦 A entregar:</div>
                            <div className="text-right font-bold text-red-700">{formatCurrency(deliverTotal)}</div>
                            
                            {electronicPayments?.summary?.total > 0 && (
                                <>
                                    <div className="text-gray-600">💳 Pagos electrónicos:</div>
                                    <div className="text-right font-bold text-teal-700">
                                        {formatCurrency(electronicPayments.summary.total)}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {/* Digital Signature PIN */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                <span>🔐</span> Firma Digital (PIN de 4 dígitos)
                            </label>
                            <div className="flex justify-center">
                                <input
                                    type="password"
                                    maxLength={4}
                                    value={pin}
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                                    className="w-32 p-3 border-2 border-gray-300 rounded-lg text-center text-3xl font-mono tracking-widest focus:border-blue-500 focus:ring-0"
                                    placeholder="••••"
                                    autoFocus
                                />
                            </div>
                            <p className="text-xs text-gray-500 text-center mt-2">
                                Este PIN generará su firma digital única para el cierre de caja.
                            </p>
                        </div>

                        {/* Optional notes */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notas (opcional):
                            </label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg"
                                rows={2}
                                placeholder="Observaciones sobre el cierre..."
                            />
                        </div>

                        {error && (
                            <div className="alert-box alert-error mb-4">{error}</div>
                        )}

                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setStep(2)}
                                className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600"
                            >
                                ← Volver
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400"
                            >
                                {loading ? '⏳ Cerrando...' : '🔐 Cerrar Caja'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default BlindClosingModal;
